// src/utils/apiClient.js
import BASE_URL from "../apiConfig";

const apiClient = async (
  endpoint,
  method = "GET",
  body = null,
  customHeaders = {}
) => {
  // Clean endpoint
  const isAbsolute = /^https?:\/\//i.test(endpoint);
  const cleanEndpoint = endpoint.replace(/^\/+/, "");
  
  let url;
  if (isAbsolute) {
    url = endpoint;
  } else {
    // If the endpoint is for the main authentication system (starts with 'quiz/'),
    // we must strip the '/compiler/' suffix from the BASE_URL to hit the root '/quiz/' paths correctly.
    if (cleanEndpoint.startsWith("quiz/")) {
      const rootBase = BASE_URL.replace(/\/compiler\/?$/, "");
      url = `${rootBase.replace(/\/$/, "")}/${cleanEndpoint}`;
    } else {
      url = `${BASE_URL.replace(/\/$/, "")}/${cleanEndpoint}`;
    }
  }

  // Automatically grab the token
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("user_token") ||
    localStorage.getItem("access_token");

  // Headers
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",

    ...customHeaders,
  };

  // Attach token automatically ONLY for internal API calls
  // Allow token for GET requests to 'login' endpoints (used for profile fetch)
  // Skip Authorization for compiler endpoints as they do not support it in CORS or don't require it
  const isCompiler = endpoint.includes("compiler/") || (url && url.includes("/compiler/"));
  
  if (token && !isAbsolute && !isCompiler && (method === "GET" || (!endpoint.includes("login") && !endpoint.includes("create-user")))) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Handle ngrok skip browser warning for any ngrok URLs (relative and absolute)
  if (url && url.includes("ngrok")) {
    headers["ngrok-skip-browser-warning"] = "true";
  }

  const options = {
    method,
    headers,
  };

  if (body && method !== "GET") {
    if (body instanceof FormData) {
      delete headers["Content-Type"]; // Let browser set it with boundary
      options.body = body;
    } else if (typeof body === "string") {
      options.body = body;
    } else {
      options.body = JSON.stringify(body);
    }
  }

  // ── MOCK DATA INTERCEPTOR FOR TEST ID 12345 ──
  if (typeof endpoint === "string" && endpoint.includes("12345")) {
    if (endpoint.includes("trainer/trainers/get/")) {
      return [{
        name: localStorage.getItem("username") || "Test Trainer",
        education: [],
        resume: "",
        current_location: "",
        native_location: ""
      }];
    }
    if (endpoint.includes("filter_by_status")) {
      return []; // Return empty list for requests
    }
  }

  try {
    const response = await fetch(url, options);

    if (response.status === 401 && !cleanEndpoint.includes("login") && !cleanEndpoint.includes("create-user")) {
      // Token is expired or invalid during an authenticated session. Clear storage and force re-login.
      localStorage.clear();
      window.location.href = "/#/LoginPage";
      return;
    }

    const contentType = response.headers.get("content-type");
    const isJson = contentType?.includes("application/json");
    let data = null;
    try {
      if (isJson) {
        data = await response.json();
      }
    } catch (parseErr) {
      console.warn("JSON parse error", parseErr);
    }

    if (!response.ok) {
      // 1. Try to find message in data
      let errorMsg = data?.message || data?.detail || data?.error;

      // 2. Handle nested objects/arrays (common in Django validation errors)
      if (!errorMsg && data && typeof data === "object" && data !== null) {
        const extractMessages = (obj) => {
          let messages = [];
          Object.entries(obj).forEach(([key, value]) => {
            if (key === "success" || key === "status") return;
            if (Array.isArray(value)) {
              messages.push(`${key}: ${value.join(" ")}`);
            } else if (typeof value === "object" && value !== null) {
              messages.push(...extractMessages(value));
            } else {
              messages.push(`${key}: ${String(value)}`);
            }
          });
          return messages;
        };
        const messages = extractMessages(data);
        if (messages.length > 0) {
          errorMsg = messages.join(" | ");
        }
      }

      // 3. Fallback to response status text or generic
      if (!errorMsg) {
        errorMsg = response.statusText || `Request failed with status ${response.status}`;
      }

      // Create a custom error object that components can use
      const error = new Error(errorMsg);
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error("API Error:", { url, method, status: error.status, message: error.message });
    throw error;
  }
};

export default apiClient;
